import React, { useEffect, useState } from 'react';
import { useLocation } from '@remix-run/react';
import {
    Container,
    useAppDisplayModeContext,
    useStyles,
    useTheme,
} from '@archipro-design/aria';
import HeaderSection from '../header-section/HeaderSection';
import * as S from './PageLayout.style';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { MobileBreadcrumb } from '../breadcrumb/Breadcrumb.mobile';
import type { PrimaryCTAResult } from '@modules/professional/util/professional-cta-helper';
import { BannerSegmentEnum } from 'generated/graphql';
import { useMatchesHandleData } from '~/modules/root';
import type { AwardProps } from '@modules/page-builder/component/detail/banner-awards/types';
import { PROFESSIONAL_SECTIONS_ANCHOR } from '../../util/constants';
import type { ProfessionalDetailsInitialFragment } from 'generated/graphql';
import CompanyProfile from '~/modules/company-profile/CompanyProfile';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import { SpecifierLoginPrompt } from '~/modules/enquiry/component/specifier-login-prompt/SpecifierLoginPrompt';

/** Map and sort banner images for the HeaderSection carousel (descending MemberSortOrder). */
const mapBannerImages = (
    bannerImages: ProfessionalDetailsInitialFragment['BannerImages'],
) =>
    bannerImages
        .filter((b) => b.BannerSegment !== BannerSegmentEnum.Aboutus)
        .map((banner) => ({
            id: banner.ID,
            bannerSegment: banner.BannerSegment,
            memberSortOrder: banner.MemberSortOrder,
            wasUpscaled: banner.WasUpscaled ?? false,
            libraryImageFile: {
                __typename: 'LibraryImageFile' as const,
                DetailBannerStandard: banner.LibraryImageFile
                    ?.DetailBannerStandard ?? {
                    __typename: 'ResponsiveImage' as const,
                    Mobile: null,
                    Desktop: null,
                },
                DetailPageSegmentBanner: banner.LibraryImageFile
                    ?.DetailPageSegmentBanner ?? {
                    __typename: 'ResponsiveImage' as const,
                    Mobile: null,
                    Desktop: null,
                },
            },
        }))
        .sort((a, b) => (b.memberSortOrder ?? 0) - (a.memberSortOrder ?? 0));

const PageLayout: React.FC<
    React.PropsWithChildren & {
        primaryCTA: PrimaryCTAResult;
        awards?: AwardProps[];
        canCarouselControl?: boolean;
    }
> = ({ children, primaryCTA, awards, canCarouselControl }) => {
    const theme = useTheme();
    const { mobile } = useAppDisplayModeContext();

    const disableMobileUserScaling = useMatchesHandleData<boolean>(
        'disableMobileUserScaling',
        false,
    );
    const noUserScalable = mobile && disableMobileUserScaling;

    const { css } = useStyles({ noUserScalable });

    const professional = useProfessionalInitialRouteData();
    const breadcrumbs = professional.Breadcrumbs;
    const bannerImages = professional.BannerImages;

    const [bannerImageList, setBannerImageList] = useState(
        mapBannerImages(bannerImages),
    );

    const { openEnquiryModal } = useEnquiryModal({
        professional: professional,
        itemType: 'professional',
        siteTreeID: professional.ID,
        itemTitle: professional.Title,
        includeBranch: true,
    });

    useEffect(() => {
        setBannerImageList(mapBannerImages(bannerImages));
    }, [bannerImages]);

    const location = useLocation();
    const { showEnquiryForm, showSpecifierLoginPrompt } =
        useEnquiryVisibility(professional);
    const canShowEnquirySection = !location.pathname.includes('contact');

    return (
        <div className={css(S.Page)}>
            <Container
                variables={{
                    background:
                        theme.siteVariables.colorScheme.default?.background,
                }}
            >
                <HeaderSection
                    banners={bannerImageList}
                    awards={awards}
                    breadcrumbs={breadcrumbs}
                    companyLogo={professional.CompanyLogo}
                    professionalId={professional.ID}
                    logoBackgroundColor={professional.LogoBackgroundColor}
                    openEnquiryModal={openEnquiryModal}
                    canCarouselControl={canCarouselControl}
                />
                <div
                    className={`pt-0 md:pt-8 ${mobile ? 'scroll-mt-63' : 'scroll-mt-32'}`}
                    id={PROFESSIONAL_SECTIONS_ANCHOR}
                >
                    {children}
                </div>
                {mobile && (
                    <div className={css(S.PageEndWrapper)}>
                        {canShowEnquirySection && (
                            <div className="mt-20 px-4.5">
                                {(showEnquiryForm ||
                                    showSpecifierLoginPrompt) && (
                                    <h3
                                        className="mt-14 pb-6 text-lg font-medium uppercase tracking-tight"
                                        style={{
                                            fontFamily:
                                                "'neue-haas-grotesk-text', sans-serif",
                                            letterSpacing: '-0.02em',
                                            color: '#343434',
                                        }}
                                    >
                                        GET IN TOUCH
                                    </h3>
                                )}
                                {showSpecifierLoginPrompt && (
                                    <SpecifierLoginPrompt
                                        professionalName={professional.Title}
                                        itemDescription="this professional"
                                        showBottomBorder={false}
                                        className="pt-0"
                                    />
                                )}
                                <CompanyProfile
                                    data={{
                                        Professional: professional,
                                        siteTreeID: professional.ID,
                                        itemType: 'professional',
                                        website: professional.Website,
                                        primaryCTA: primaryCTA,
                                    }}
                                    showViewProfile={false}
                                    showEnquiryForm={showEnquiryForm}
                                    includeBranch
                                />
                            </div>
                        )}
                        <MobileBreadcrumb breadcrumbs={breadcrumbs} />
                    </div>
                )}
            </Container>
        </div>
    );
};

export default PageLayout;
